import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MenuItemAction } from 'menu/MenuItem';
import { showContextMenu } from 'redux/mode/actions';
import { selectIsMobile } from 'redux/mode/reducers';
import { ChatEntry, Attachment } from 'dragonmouse-shared/interfaces';
import { FileUploadCallbacks, maybeUploadPastedText, onFilePasted } from 'utils';

interface UseChatInputHandlersOptions {
	prompt: string;
	inputRef: React.RefObject<HTMLTextAreaElement>;
	canSubmit: boolean;
	onSubmit: () => void;
	onUpdatePrompt: (prompt: string) => void;
	fileCallbacks: FileUploadCallbacks;
	chatEntries: ChatEntry[];
	pendingAttachments?: Attachment[];
	// Called before default paste behavior. Return true to suppress default text insertion.
	onBeforePaste?: (text: string) => boolean;
}

interface UseChatInputHandlersResult {
	onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
	onPaste: (e: React.ClipboardEvent) => Promise<void>;
	handlePaste: (text: string) => void;
	onContextMenu: (e: React.MouseEvent) => Promise<void>;
}

// Collects all attachment filenames from chat entries and pending attachments.
function getExistingFilenames(entries: ChatEntry[], pending?: Attachment[]): string[] {
	const names: string[] = [];
	for (const entry of entries) {
		if ('attachments' in entry && entry.attachments) {
			for (const a of entry.attachments) names.push(a.filename);
		}
	}
	if (pending) {
		for (const a of pending) names.push(a.filename);
	}
	return names;
}

export function useChatInputHandlers({
	prompt,
	inputRef,
	canSubmit,
	onSubmit,
	onUpdatePrompt,
	fileCallbacks,
	chatEntries,
	pendingAttachments,
	onBeforePaste,
}: UseChatInputHandlersOptions): UseChatInputHandlersResult {
	const dispatch = useDispatch();
	const isMobile = useSelector((r: any) => selectIsMobile(r.mode));

	const onKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLElement>) => {
			// On desktop: Enter submits, Shift+Enter adds line break
			// On mobile: Enter adds line break, user taps button to submit
			if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
				e.preventDefault();
				if (canSubmit) {
					onSubmit();
				}
			}
		},
		[isMobile, canSubmit, onSubmit]
	);

	const handlePaste = useCallback(
		(text: string) => {
			if (onBeforePaste?.(text)) return;
			if (
				maybeUploadPastedText(
					text,
					getExistingFilenames(chatEntries, pendingAttachments),
					fileCallbacks
				)
			)
				return;

			// Insert the pasted text at the current cursor position in the prompt
			const textarea = inputRef.current;
			if (!textarea) {
				// Fallback to appending if ref is not available
				const newPrompt = prompt + text;
				onUpdatePrompt(newPrompt);
				return;
			}

			const cursorPosition = textarea.selectionStart || prompt.length;
			const newPrompt = prompt.slice(0, cursorPosition) + text + prompt.slice(cursorPosition);
			const newCursorPosition = cursorPosition + text.length;

			onUpdatePrompt(newPrompt);

			// Focus the input and set cursor position after pasting
			setTimeout(() => {
				if (textarea) {
					textarea.focus();
					textarea.setSelectionRange(newCursorPosition, newCursorPosition);
				}
			}, 0);
		},
		[
			prompt,
			inputRef,
			onUpdatePrompt,
			onBeforePaste,
			chatEntries,
			pendingAttachments,
			fileCallbacks,
		]
	);

	const onPaste = useCallback(
		async (e: React.ClipboardEvent) => {
			const text = e.clipboardData?.getData('text/plain');

			if (text && onBeforePaste?.(text)) {
				e.preventDefault();
				return;
			}

			if (
				text &&
				maybeUploadPastedText(
					text,
					getExistingFilenames(chatEntries, pendingAttachments),
					fileCallbacks
				)
			) {
				e.preventDefault();
				return;
			}

			await onFilePasted(fileCallbacks)(e);
		},
		[onBeforePaste, chatEntries, pendingAttachments, fileCallbacks]
	);

	const onContextMenu = useCallback(
		async (e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();

			if (e.target instanceof HTMLElement) {
				const paste = await MenuItemAction.createPasteText(handlePaste);
				const menuItems = [paste];
				dispatch(
					showContextMenu({
						point: { x: e.clientX + 5, y: e.clientY },
						menuItems,
					})
				);
			}
		},
		[handlePaste, dispatch]
	);

	return {
		onKeyDown,
		onPaste,
		handlePaste,
		onContextMenu,
	};
}
